
const rootReferenceFrame = {
    o: {x: 0, y: 0},
    i: {x: 1, y: 0},
    localLengthToGlobal: 1
}


const degreesToRadians = Math.PI / 180
const radiansToDegrees = 1 / degreesToRadians


function ReferenceFrame (params) {
    this.initialize2D(params)
}

addLocal2DCapabilities(ReferenceFrame.prototype)



function initialize2D ({relativeTo: referenceFrame = rootReferenceFrame, x, y, orientation, scale = 1, link} = {}) { //eslint-disable-line complexity

    if (typeof referenceFrame === "function") {
        referenceFrame = referenceFrame()
    }

    if (!("localLengthToGlobal" in referenceFrame)) {
        referenceFrame = new ReferenceFrame(referenceFrame)
    }

    this.referenceFrame     = referenceFrame

    this.xAsChild           = typeof x === "function"           ? x()           : (x || 0)
    this.yAsChild           = typeof y === "function"           ? y()           : (y || 0)
    this.orientationAsChild = typeof orientation === "function" ? orientation() : (orientation || 0)
    this.scaleAsChild       = typeof scale === "function"       ? scale()       : scale

    initVectorFrame(this)

    if (link && referenceFrame) {
        if ("children" in referenceFrame) {
            referenceFrame.children.push(this)
        } else {
            referenceFrame.children = [this]
        }
    }

}



function updateReference ({x = null, y = null, orientation = null, scale = null} = {}) { //eslint-disable-line complexity

    if (x !== null) {
        this.xAsChild = x
    }
    if (y !== null) {
        this.yAsChild = y
    }
    if (orientation !== null) {
        this.orientationAsChild = orientation
    }
    if (scale !== null) {
        this.scaleAsChild = scale
    }

    initVectorFrame(this)

    delete this._cachedFrameGlobalOrientation
    delete this._inverseSquarelocalLengthToGlobal


    if (this.children) {
        for (let child of this.children) {
            child.updateReference()
        }
    }
}


// only for convenience or display, not used in computations
function j () {
    let {i} = this
    return {
        x: -i.y,
        y: i.x
    }
}




// localLength(otherFrame.globalLength(lengthInThatFrame)) is 6 times slower
function lengthFromFrame (otherFrame, lengthInThatFrame) {
    return lengthInThatFrame * otherFrame.localLengthToGlobal / this.localLengthToGlobal
}


function globalLength (localLengthValue) {
    return this.localLengthToGlobal * localLengthValue
}


function localLength (globalLengthValue) {
    return globalLengthValue / this.localLengthToGlobal
}







function orientationFromFrame (otherFrame, orientationInThatFrame) {
    return frameGlobalOrientation(otherFrame) + orientationInThatFrame - frameGlobalOrientation(this)
}


function globalOrientation (localOrientationValue = 0) {
    return frameGlobalOrientation(this) + localOrientationValue
}


function localOrientation (globalOrientationValue = 0) {
    return globalOrientationValue - frameGlobalOrientation(this)
}




// not DRY with globalVector because of optimization purposes
function vectorFromFrame (otherFrame, xInThatFrame, yInThatFrame) {
    let {i} = otherFrame
    let globalX = i.x * xInThatFrame - i.y * yInThatFrame
    let globalY = i.y * xInThatFrame + i.x * yInThatFrame
    return this.localVector(globalX, globalY)
}


function localVector (globalX, globalY) {
    if (!this._inverseSquareLocalLengthToGlobal) {
        this._inverseSquareLocalLengthToGlobal = 1 / (this.localLengthToGlobal * this.localLengthToGlobal)
    }

    let {i} = this
    return {
        x: (i.x * globalX + i.y * globalY) * this._inverseSquareLocalLengthToGlobal,
        y: (i.x * globalY - i.y * globalX) * this._inverseSquareLocalLengthToGlobal
    }
}




// not DRY with globalPoint because of optimization purposes
function pointFromFrame (otherFrame, xInThatFrame, yInThatFrame) {
    let {i, o} = otherFrame
    let globalX = o.x + i.x * xInThatFrame - i.y * yInThatFrame
    let globalY = o.y + i.y * xInThatFrame + i.x * yInThatFrame
    return this.localVector(
        globalX - this.o.x,
        globalY - this.o.y
    )
}


function localPoint (globalX, globalY) {
    return this.localVector(
        globalX - this.o.x,
        globalY - this.o.y
    )
}




function pointsFromFrame (...thatFramePoints) {
    let otherFrame = thatFramePoints.shift()
    return thatFramePoints.map(point => this.pointFromFrame(otherFrame, point[0], point[1]))
}


function globalPoints (...points) {
    return points.map(point => globalPoint(this, point[0], point[1]))
}


function localPoints (...points) {
    return points.map(point => this.localPoint(point[0], point[1]))
}




function globalBase (localBase) {
    if ("localLengthToGlobal" in localBase) {
        const {x, y} = localBase.o
        return new ReferenceFrame({
            x,
            y,
            orientation: frameGlobalOrientation(localBase),
            scale:       localBase.localLengthToGlobal,
            relativeTo:  this
        })
    } else {
        const {x, y, orientation, scale} = localBase
        return new ReferenceFrame({x, y, orientation, scale, relativeTo: this})
    }
}



function addLocal2DCapabilities (object) {


    Object.assign(object, {
        initialize2D,
        updateReference,
        j,

        lengthFromFrame,
        globalLength,
        localLength,

        orientationFromFrame,
        globalOrientation,
        localOrientation,

        vectorFromFrame,
        globalVector: function (localX, localY) {
            return globalVector(this, localX, localY)
        },
        localVector,

        pointFromFrame,
        globalPoint: function (localX, localY) {
            return globalPoint(this, localX, localY)
        },
        localPoint,

        pointsFromFrame,
        globalPoints,
        localPoints,

        globalBase
    })


}




function globalVector (referenceFrame, localX, localY) {
    let {i} = referenceFrame
    return {
        x: i.x * localX - i.y * localY,
        y: i.y * localX + i.x * localY
    }
}


function globalPoint (referenceFrame, localX, localY) {
    let {o, i} = referenceFrame
    return {
        x: o.x + i.x * localX - i.y * localY,
        y: o.y + i.y * localX + i.x * localY
    }
}


function initVectorFrame (object) {

    let {referenceFrame, orientationAsChild, scaleAsChild} = object

    let radOrientation = degreesToRadians * orientationAsChild

    object.o = globalPoint(
        referenceFrame,
        object.xAsChild,
        object.yAsChild
    )

    object.i = globalVector(
        referenceFrame,
        scaleAsChild * Math.cos(radOrientation),
        scaleAsChild * Math.sin(radOrientation)
    )


    if ("localLengthToGlobal" in referenceFrame) {
        object.localLengthToGlobal = referenceFrame.localLengthToGlobal * scaleAsChild
    } else {
        let {x, y} = object.i
        object.localLengthToGlobal = Math.sqrt(x * x + y * y)
    }

}



function frameGlobalOrientation (object) {
    if (!("_cachedFrameGlobalOrientation" in object)) {
        let {i} = object
        object._cachedFrameGlobalOrientation = Math.atan2(i.y, i.x) * radiansToDegrees
    }
    return object._cachedFrameGlobalOrientation
}



function referenceFrameFromGroupParams (params) { //eslint-disable-line complexity

    const {x, y, orientation, scale = 1} = params

    if (x || y || orientation || (scale !== 1)) {
        return new ReferenceFrame(params)
    }

    let {relativeTo: referenceFrame} = params
    if (typeof referenceFrame === "function") {
        referenceFrame = referenceFrame()
    }

    if (referenceFrame && !("localLengthToGlobal" in referenceFrame)) {
        return new ReferenceFrame(referenceFrame)
    } else {
        return referenceFrame
    }
}


function referenceFrameFromScreenObject (object) {
    let {x, y, orientation, scale} = object.viewPosition
    let {relativeTo} = object
    return new ReferenceFrame({x, y, orientation, scale, relativeTo})
}




//FIXME DRY / optimize
function globalPointFromReferenceFrame (referenceFrame, localX, localY) {
    if ("globalPoint" in referenceFrame) {
        return referenceFrame.globalPoint(localX, localY)
    }

    let {scale = 1, orientation = 0, x = 0, y = 0} = referenceFrame

    if (orientation === 0) {
        return {
            x: x + scale * localX,
            y: y + scale * localY
        }
    } else {
        const orientationRad = orientation * degreesToRadians
        let cosOrientation = Math.cos(orientationRad)
        let sinOrientation = Math.sin(orientationRad)
        return {
            x: x + scale * (localX * cosOrientation - localY * sinOrientation),
            y: y + scale * (localX * sinOrientation + localY * cosOrientation)
        }
    }
}


//FIXME DRY / optimize
function localPointInReferenceFrame (referenceFrame, globalX, globalY) {
    if (typeof referenceFrame === "function") {
        referenceFrame = referenceFrame()
    }

    if ("localPoint" in referenceFrame) {
        return referenceFrame.localPoint(globalX, globalY)
    }

    let {scale = 1, orientation = 0, x = 0, y = 0, relativeTo} = referenceFrame

    if (relativeTo) { //FIXME optimize ?
        ({x: globalX, y: globalY} = localPointInReferenceFrame(relativeTo, globalX, globalY))
    }

    if (orientation === 0) {
        return {
            x: (globalX - x) / scale,
            y: (globalY - y) / scale
        }
    } else {
        const orientationRad = orientation * degreesToRadians
        let cosOrientation = Math.cos(orientationRad)
        let sinOrientation = Math.sin(orientationRad)
        let tempX = (globalX - x) / scale
        let tempY = (globalY - y) / scale
        return {
            x:  tempX * cosOrientation + tempY * sinOrientation,
            y: -tempX * sinOrientation + tempY * cosOrientation
        }
    }

}


export default {
    ReferenceFrame,
    addLocal2DCapabilities,
    referenceFrameFromGroupParams,
    referenceFrameFromScreenObject,
    globalPointFromReferenceFrame,
    localPointInReferenceFrame
}


